import {
  DecksDocument,
  DecksQuery,
  PlayingCardManufacturersDocument,
  PlayingCardManufacturersQuery,
  ProductByCategoryFilter,
  ProductByCategorySort,
} from '@/generated/graphql'
import { graphqlRequest } from '@/lib/serverSideClient'
import { sortFields } from './SortMenu/helpers'
import { isManufacturerSlug } from '@/playingCardManufacturerSlugs'

export interface ParsedPlayingCardsUrl {
  playingCardManufacturerSlug: string
  sortSlug: string
  showAll: boolean
  page: number
}

const EMPTY_MANUFACTURERS: PlayingCardManufacturersQuery = {
  playingCardManufacturers: [],
}

const EMPTY_DECKS: DecksQuery = {
  productsByCategorySlug: {
    totalCount: 0,
    items: [],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
    },
  },
}

/**
 * Fetches all playing card manufacturers from the GraphQL API.
 * Returns empty array on error to prevent infinite loops.
 *
 * @returns Promise resolving to manufacturers query result
 */
export const getPlayingCardManufacturers =
  async (): Promise<PlayingCardManufacturersQuery> => {
    try {
      return await graphqlRequest<PlayingCardManufacturersQuery>(
        PlayingCardManufacturersDocument
      )
    } catch {
      return EMPTY_MANUFACTURERS
    }
  }

/**
 * Fetches playing card products (decks) from the GraphQL API.
 * Returns empty result on error to prevent page crashes.
 *
 * @param brandCode - Brand code (VANISHING_INC_US or VANISHING_INC_UK)
 * @param categorySlug - Category slug (should be 'playing-cards')
 * @param page - Current page number (1-indexed)
 * @param perPage - Number of items per page
 * @param order - Sort order configuration (ProductByCategorySort or array)
 * @param where - Filter conditions
 * @returns Promise resolving to decks query result
 */
export const getPlayingCardDecks = async (
  brandCode: string,
  categorySlug: string,
  page: number,
  perPage: number,
  order: ProductByCategorySort | undefined,
  where: ProductByCategoryFilter,
  inStockOnly: boolean
): Promise<DecksQuery> => {
  try {
    return await graphqlRequest<DecksQuery>(DecksDocument, {
      take: perPage,
      skip: (page - 1) * perPage,
      brandCode,
      includeChildCategories: true,
      categorySlug,
      order: order ? [order] : undefined,
      where,
      inStockOnly,
    })
  } catch {
    return EMPTY_DECKS
  }
}

/**
 * Parses the URL slug list for playing cards category pages.
 * Handles the structures:
 * - /brand/{manufacturer}/[sort]/[all]/[page]
 * - /all/[sort]/[all]/[page]
 * - /sort/[all]/[page] (legacy)
 *
 * Legacy direct manufacturer URLs (/manufacturer/[sort]/[all]/[page])
 * are redirected to /brand/{manufacturer}/... in middleware, but are still
 * parsed defensively here.
 *
 * @param slugList - Array of URL slug segments
 * @returns Parsed URL parameters
 */
export const parsePlayingCardsUrl = (
  slugList: string[]
): ParsedPlayingCardsUrl => {
  // Start with defaults
  let playingCardManufacturerSlug = 'all'
  let sortSlug = 'newest'
  let showAll = false
  let page = 1

  const parseTrailingSegments = (segmentIndex: number) => {
    // Check if next segment is a sort field
    if (
      slugList[segmentIndex] &&
      sortFields.some(f => f.slug === slugList[segmentIndex])
    ) {
      sortSlug = slugList[segmentIndex]
      segmentIndex++
    }

    // Check if next segment is 'all' filter
    if (slugList[segmentIndex] === 'all') {
      showAll = true
      segmentIndex++
    }

    // Check if next segment is a page number
    if (slugList[segmentIndex] && !isNaN(Number(slugList[segmentIndex]))) {
      const parsedPage = Number(slugList[segmentIndex])
      if (parsedPage > 0) {
        page = parsedPage
      }
    }
  }

  // Only process if we have at least one slug
  if (slugList.length > 0 && slugList[0]) {
    if (slugList[0] === 'brand' && slugList[1]) {
      playingCardManufacturerSlug = slugList[1]
      parseTrailingSegments(2)
    }
    // Check if first slug is a known manufacturer or "all"
    else if (slugList[0] === 'all' || isManufacturerSlug(slugList[0])) {
      playingCardManufacturerSlug = slugList[0]
      // '/playing-cards/all/' is the canonical "show all decks" URL.
      // For manufacturer-specific pages, default remains in-stock only.
      showAll = slugList[0] === 'all'

      // Parse remaining segments sequentially
      parseTrailingSegments(1)
    } else {
      // First slug is not a manufacturer - might be a sort field (old format)
      // Default to All brand and treat first slug as sort
      playingCardManufacturerSlug = 'all'
      const isSortField = sortFields.some(f => f.slug === slugList[0])
      if (isSortField) {
        sortSlug = slugList[0]

        // Check for 'all' or page in next segments
        if (slugList[1] === 'all') {
          showAll = true
          if (slugList[2] && !isNaN(Number(slugList[2]))) {
            const parsedPage = Number(slugList[2])
            if (parsedPage > 0) {
              page = parsedPage
            }
          }
        } else if (slugList[1] && !isNaN(Number(slugList[1]))) {
          const parsedPage = Number(slugList[1])
          if (parsedPage > 0) {
            page = parsedPage
          }
        }
      }
    }
  }

  return {
    playingCardManufacturerSlug,
    sortSlug,
    showAll,
    page,
  }
}

/**
 * Calculates the lowest price from all product options, considering sale prices.
 *
 * @param options - Array of product options with price and salePrice
 * @param fallbackPrice - Fallback price if no options found
 * @returns Object with lowest regular price, sale price (if applicable), and the option with lowest price
 */
export const calculateLowestPrice = (
  options: Array<{
    price: number | null | undefined
    salePrice?: number | null | undefined
    priceIncVat?: number | null | undefined
    salePriceIncVat?: number | null | undefined
    taxType?: number | null | undefined
  }>,
  fallbackPrice: number | null
): {
  lowestRegularPrice: number | null
  lowestSalePrice: number | null
  lowestPriceOption: {
    priceIncVat?: number | null | undefined
    salePriceIncVat?: number | null | undefined
    taxType?: number | null | undefined
  } | null
} => {
  let lowestRegularPrice: number | null = null
  let lowestSalePrice: number | null = null
  let lowestPriceOption: {
    priceIncVat?: number | null | undefined
    salePriceIncVat?: number | null | undefined
    taxType?: number | null | undefined
  } | null = null
  let lowestEffectivePrice: number | null = null

  for (const option of options) {
    const regularPrice = option.price ?? null
    const salePrice = option.salePrice ?? null
    const effectivePrice =
      salePrice !== null && regularPrice !== null && salePrice < regularPrice
        ? salePrice
        : regularPrice

    if (
      effectivePrice !== null &&
      (lowestEffectivePrice === null || effectivePrice < lowestEffectivePrice)
    ) {
      lowestEffectivePrice = effectivePrice
      lowestRegularPrice = regularPrice
      lowestSalePrice =
        salePrice !== null && regularPrice !== null && salePrice < regularPrice
          ? salePrice
          : null
      lowestPriceOption = option
    }
  }

  // Fallback to displayPrice if no options found
  if (lowestRegularPrice === null) {
    lowestRegularPrice = fallbackPrice
  }

  return {
    lowestRegularPrice,
    lowestSalePrice,
    lowestPriceOption,
  }
}

/**
 * Builds a clean URL for playing cards category pages.
 * Omits default values to keep URLs clean.
 *
 * @param brand - Manufacturer slug or 'all' for all brands
 * @param sort - Sort field slug (defaults omitted if 'newest')
 * @param showAllFilter - Whether to show out-of-stock items
 * @param pageNum - Page number (defaults omitted if 1)
 * @returns Clean URL path
 */
export const buildUrl = (
  brand: string,
  sort: string,
  showAllFilter: boolean,
  pageNum: number
) => {
  // Main in-stock default view
  if (brand === 'all' && sort === 'newest' && !showAllFilter && pageNum === 1) {
    return '/playing-cards/'
  }

  const parts = ['/playing-cards']

  if (brand === 'all') {
    if (showAllFilter) {
      parts.push('all')
      if (sort !== 'newest') {
        parts.push(sort)
      }
    } else if (sort !== 'newest') {
      parts.push(sort)
    } else if (pageNum !== 1) {
      parts.push(sort)
    }
  } else {
    parts.push('brand', brand)
    if (sort !== 'newest') {
      parts.push(sort)
    }
    if (showAllFilter) {
      parts.push('all')
    }
  }

  if (pageNum !== 1) {
    parts.push(pageNum.toString())
  }

  return parts.join('/')
}
